'use client';

import { useEffect, useId, useRef, useState } from 'react';
import type { TriggerMarker } from './droneMapTypes';

interface TriggerPointCardProps {
  /**
   * The LIVE row, resolved by id against the current trigger list by the map -
   * never a snapshot. When another operator answers this detection the row
   * disappears and the map stops rendering this card entirely.
   */
  trigger: TriggerMarker;
  /** Wall clock when the card opened; the age is frozen to it. */
  openedAt: number;
  /** Absent = this operator may not dispatch; the card then offers only Dismiss. */
  onDispatch?: (trigger: TriggerMarker) => void;
  onDismiss?: (id: string) => void;
  /**
   * Absent for the same reason as `onDispatch`: approving launches an aircraft.
   *
   * Awaited. The card stays open, disabled, saying what is happening until it settles - see
   * `APPROVE_ARM_MS` and the `approving` state below.
   */
  onApprove?: (id: string) => void | Promise<void>;
  onReject?: (id: string) => void;
  onClose: () => void;
}

/**
 * How long the Approve row refuses a click after the proposal first appears on an open card.
 *
 * A CLICK ALREADY IN MOTION MUST NOT LAUNCH AN AIRCRAFT (review, 2026-09-12). The dispatcher
 * proposes within a second or two of the detection, and the card is rendered from the LIVE row
 * with stable identity - so a proposal arriving into an already-open card inserts its block plus
 * two rows in place, and `anchoredCardPosition` re-centres the card on the marker, moving every
 * row. The operator's reflex is to click the fresh red point and then `Dispatch drone here...`,
 * the first action row; a moment later `Approve and launch BRAVO` is roughly where that row was.
 * There is no confirm on it, unlike `DroneCommandBar`'s takeoff.
 *
 * 800 ms is the gap between "the layout moved under me" and "I decided to press this". It only
 * applies to a proposal that ARRIVES while the card is open - one that was already on the row
 * when the card opened is armed immediately, because nothing moved.
 */
const APPROVE_ARM_MS = 800;

/** Whole minutes, because a detection's age is read at a glance, not measured. */
function ageLabel(occurredAt: string, now: number): string {
  const seconds = Math.round((now - new Date(occurredAt).getTime()) / 1000);
  if (!Number.isFinite(seconds)) return '';
  if (seconds < 60) return `${Math.max(0, seconds)}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

/**
 * The aircraft's operator-facing name, or null when the row cannot name it.
 *
 * NULL IS A REAL ANSWER AND THE CALLER MUST HONOUR IT. The proposal stores an id; the callsign
 * lives in the `considered` list the selector recorded. If that list is missing, the card must
 * NOT invite an operator to approve an unnamed airframe - propose-and-approve exists so that a
 * human approves a NAMED aircraft, and "Approve and launch an aircraft" is not that.
 */
function proposedLabel(trigger: TriggerMarker): string | null {
  const id = trigger.proposed_drone_id;
  if (!id) return null;
  const entry = trigger.proposal_considered?.find((c) => c.droneId === id);
  return entry?.callsign ?? entry?.name ?? null;
}

/** The aircraft that were weighed and refused, most useful first. */
function skipped(trigger: TriggerMarker) {
  return (trigger.proposal_considered ?? []).filter((c) => c.rejection !== null);
}

/** Whole seconds/minutes to the proposal lapsing, or null when it does not lapse. */
function expiresLabel(expiresAt: string | null | undefined, now: number): string | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return 'expired';
  const seconds = Math.round(ms / 1000);
  if (seconds < 90) return `${seconds}s left`;
  return `${Math.round(seconds / 60)}m left`;
}

/** Every button on this card. Hand-rolled, so the focus ring and the cursor are stated here. */
const ROW = 'w-full text-left px-3 py-2 transition-colors cursor-pointer outline-none '
  + 'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2';

/**
 * The card an operator gets by clicking a trigger point: what was seen, how
 * confident, how long ago, and the things they can do about it.
 *
 * WHEN THE PLATFORM HAS PROPOSED AN AIRCRAFT the card leads with that instead - the aircraft's
 * name, the altitude, how long the offer stands, and Approve/Reject - because the operator's
 * decision is no longer "who should go" but "yes or no to this one". The manual Dispatch path
 * stays available underneath: rejecting the proposal and choosing an aircraft by hand are
 * different actions, and an operator who wants the second should not have to do the first.
 *
 * THE THREE CLOSING VERBS ARE NOT INTERCHANGEABLE, and the labels say so rather than leaving it
 * to a code comment: Reject withdraws the aircraft and KEEPS the detection, Clear this detection
 * destroys it, Close does nothing. Rendered as three identical grey rows they read as synonyms at
 * 2am - and "Dismiss" directly above "Close" actively invites the wrong reading - so the
 * consequence is in the label and the one irreversible row is tinted and separated.
 *
 * Positioned by the map (projected from the trigger's own coordinates and
 * clamped inside the container) - this component only draws.
 */
export function TriggerPointCard({
  trigger,
  openedAt,
  onDispatch,
  onDismiss,
  onApprove,
  onReject,
  onClose,
}: TriggerPointCardProps) {
  const age = ageLabel(trigger.occurred_at, openedAt);
  const ref = useRef<HTMLDivElement | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const skippedListId = useId();

  // Focus on open so a screen reader announces the card and Escape has
  // somewhere to land. The sibling context menu does the same; without it the
  // card claims `role="dialog"` while focus stays on the map canvas.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  // A proposal is only ACTIONABLE while it is the propose-and-approve kind AND the row can name
  // the aircraft. A `full_auto` row has already flown - it reaches `dispatched` in the same breath
  // - so it can only appear here in the moment before the resolve broadcast lands, and offering
  // Approve on it would answer a question nobody asked.
  const callsign = proposedLabel(trigger);
  const hasProposal = callsign !== null && trigger.proposal_mode === 'propose';

  // ARMING. A proposal that was ALREADY on the row when the card opened moved nothing, so it is
  // live from the first render - that is the ordinary case and it must cost the operator nothing.
  // One that ARRIVES into an open card re-laid the card out under the pointer, and waits.
  //
  // The effect depends on `hasProposal` alone, deliberately: a dependency that the effect itself
  // sets would make React run the cleanup and cancel the very timer it had just armed.
  const standingAtOpen = useRef(hasProposal);
  const [armed, setArmed] = useState<boolean>(hasProposal);
  useEffect(() => {
    if (!hasProposal) { setArmed(false); return; }
    if (standingAtOpen.current) { setArmed(true); return; }
    setArmed(false);
    const t = setTimeout(() => setArmed(true), APPROVE_ARM_MS);
    return () => clearTimeout(t);
  }, [hasProposal]);

  // The launch is a server round trip through the whole bay sequence. The card holds itself open
  // and says so rather than closing on the click - see `onApprove`.
  const [approving, setApproving] = useState(false);

  // A SETTLED LAUNCH MUST NOT CLOSE SOMEBODY ELSE'S CARD (re-review). `onClose` clears the open
  // card unconditionally, and the approve promise outlives this component: approve A, press
  // Close, click point B, and forty seconds later A's launch resolves and shuts B's card under
  // the operator's hand. The `key` on this component makes the A-to-B switch a real remount, so
  // a mounted flag is all it takes to tell "my card is still the open one" from "it is not".
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const passedOver = skipped(trigger);
  const expiry = hasProposal ? expiresLabel(trigger.proposal_expires_at, openedAt) : null;
  const refusal = !hasProposal ? trigger.dispatch_refusal : null;

  // The card grows when the skipped list opens, and when a proposal arrives into an already-open
  // card. The map watches for that with a ResizeObserver and re-clamps - see DroneMap - so this
  // component does not have to tell anyone it changed size.

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-label={`Person detected, ${trigger.score != null ? `${Math.round(trigger.score)} percent confidence` : 'confidence not reported'}`}
      tabIndex={-1}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      className="surface-chrome rounded-lg py-1 min-w-[220px] max-w-[300px] max-h-[70vh] overflow-y-auto text-sm outline-none"
    >
      <div className="px-3 py-1.5 border-b divider">
        <div className="text-[11px] uppercase tracking-wider text-status-red-fg font-semibold">
          Person detected
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {trigger.score != null
            // Mono on the number only: it is telemetry. The label above it is not.
            ? (<><span className="font-mono tabular-nums">{Math.round(trigger.score)}%</span> confidence</>)
            : 'Confidence not reported'}
          {age ? <> · <span className="font-mono tabular-nums">{age}</span></> : null}
        </div>
      </div>

      {/* Announced: a proposal can arrive into a card that is already open, and a screen-reader
          user would otherwise be told nothing at all about a new launch affordance. */}
      {hasProposal && (
        <div role="status" className="px-3 py-2 border-b divider">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11px] uppercase tracking-wider text-status-amber-fg font-semibold">
              Awaiting approval
            </span>
            {expiry && (
              // The offer lapses on its own after ten minutes. An operator weighing it against
              // other traffic is entitled to know whether they have nine minutes or nine seconds.
              <span className="text-[11px] text-muted-foreground font-mono tabular-nums">{expiry}</span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Send <span className="callsign text-foreground">{callsign}</span>
            {trigger.proposed_altitude_m != null && (
              <> to hold at <span className="font-mono tabular-nums text-foreground">{Math.round(trigger.proposed_altitude_m)} m</span></>
            )}
          </div>
          {passedOver.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowSkipped((v) => !v)}
                aria-expanded={showSkipped}
                aria-controls={skippedListId}
                // py-1.5 rather than a bare 11px line: SC 2.5.8 asks for 24px, and this is the
                // only route to the justification for the aircraft the platform picked.
                className="mt-1.5 py-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer underline decoration-dotted underline-offset-2 outline-none focus-visible:outline focus-visible:outline-2"
              >
                {showSkipped ? 'Hide' : 'Why not'} the other {passedOver.length === 1 ? 'aircraft' : `${passedOver.length} aircraft`}
              </button>
              <ul id={skippedListId} className="mt-1 space-y-0.5" hidden={!showSkipped}>
                {passedOver.map((c) => (
                  <li key={c.droneId} className="text-[11px] text-muted-foreground">
                    <span className="callsign">{c.callsign ?? c.name}</span>
                    {c.detail ? <> — {c.detail}</> : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* The platform tried and nothing flew. Under full_auto nobody watched it happen, so this
          is the only account an operator gets that the detection WAS acted on - without it an
          armed policy that refuses everything is indistinguishable from one that is off. */}
      {refusal && (
        <div role="status" className="px-3 py-2 border-b divider">
          <div className="text-[11px] uppercase tracking-wider text-status-amber-fg font-semibold">
            Nothing was sent
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{refusal}</div>
          {passedOver.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {passedOver.map((c) => (
                <li key={c.droneId} className="text-[11px] text-muted-foreground">
                  <span className="callsign">{c.callsign ?? c.name}</span>
                  {c.detail ? <> — {c.detail}</> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {hasProposal && onApprove && (
        <button
          type="button"
          disabled={!armed || approving}
          aria-disabled={!armed || approving}
          onClick={() => {
            if (!armed || approving) return;
            setApproving(true);
            // Held open until the launch settles. On failure the card STAYS open and re-arms:
            // the toast says what went wrong and the offer is still standing, so the operator
            // can try again without hunting for the point a second time.
            void Promise.resolve(onApprove(trigger.id))
              .then(() => { if (mounted.current) onClose(); })
              .catch(() => { if (mounted.current) setApproving(false); });
          }}
          className={`${ROW} text-foreground row-hover font-semibold disabled:opacity-60 disabled:cursor-default`}
        >
          {approving
            // Named, because the bay sequence is the slow part and saying so is what tells the
            // operator this is a sequence running rather than a hung request to dismiss - the
            // same reasoning QuickDispatchDialog's "Opening bay..." is written from.
            ? <>Opening bay — launching <span className="callsign">{callsign}</span>…</>
            : <>Approve and launch {callsign}</>}
        </button>
      )}
      {hasProposal && onReject && (
        <button
          type="button"
          disabled={approving}
          aria-disabled={approving}
          onClick={() => { if (approving) return; onReject(trigger.id); onClose(); }}
          className={`${ROW} text-muted-foreground row-hover hover:text-foreground disabled:opacity-60 disabled:cursor-default`}
        >
          Reject {callsign} — keep the detection
        </button>
      )}

      {/* EVERY VERB THAT ACTS ON THE ROW IS HELD WHILE A LAUNCH IS RUNNING (re-review). The card
          now stays mounted for the whole bay sequence, so these are reachable in a way they were
          not before. `Clear this detection` is the dangerous one: `resolve`'s guard accepts
          `status IN ('new','proposed')` and an approved-but-launching row is still `proposed`, so
          a dismiss lands - and the in-flight stamp then finds the row `dismissed`, returns null,
          and the flying aircraft drops out of `findDispatchesAwaitingRecall` for good. Close is
          left live: putting the card away is not an action on the sortie. */}
      {onDispatch && (
        <button
          type="button"
          disabled={approving}
          aria-disabled={approving}
          onClick={() => { if (approving) return; onDispatch(trigger); onClose(); }}
          className={`${ROW} text-muted-foreground row-hover hover:text-foreground disabled:opacity-60 disabled:cursor-default`}
        >
          {hasProposal ? 'Send a different drone…' : 'Dispatch drone here…'}
        </button>
      )}

      {/* The one irreversible verb, separated and tinted so it cannot be read as "close the
          card" - which is exactly how a grey "Dismiss" directly above a grey "Close" reads. */}
      {onDismiss && (
        <button
          type="button"
          disabled={approving}
          aria-disabled={approving}
          onClick={() => { if (approving) return; onDismiss(trigger.id); onClose(); }}
          className={`${ROW} border-t divider text-status-red-fg row-hover disabled:opacity-60 disabled:cursor-default`}
        >
          Clear this detection
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className={`${ROW} text-muted-foreground row-hover hover:text-foreground`}
      >
        Close
      </button>
    </div>
  );
}
